import { useCallback, useEffect, useMemo, useState } from "react";

import { makeRequest } from "../../utils/request";
import { Badge, Button, EmptyState, InputField, Spinner, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";

type ServicePrincipal = {
  id: string;
  principal: string;
  name: string;
  description: string | null;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
};

type PermissionOption = {
  permission: string;
  group: string;
  description: string;
  internal: boolean;
  deprecated: boolean;
};

type ListResponse = { data: ServicePrincipal[]; permissions: PermissionOption[] };

const EMPTY_FORM = { principal: "", name: "", description: "", permissions: [] as string[] };

/**
 * Platform admin management of service principals — the Workload Identity
 * callers (GSA emails) allowed on service-authenticated API endpoints. They
 * hold permissions from the SAME registry as org service accounts and PATs
 * (including internal machine-only entries), but with no organization
 * binding. Changes take effect without a deploy.
 */
export const AdminServicePrincipalsPage = () => {
  const [principals, setPrincipals] = useState<ServicePrincipal[]>([]);
  const [permissionOptions, setPermissionOptions] = useState<PermissionOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create/edit form state; editingId null = create mode, undefined = closed.
  const [editingId, setEditingId] = useState<string | null | undefined>(undefined);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsLoading(true);
    try {
      const response = await makeRequest<ListResponse>("/v1/admin/service-principals");
      setPrincipals(response.data);
      setPermissionOptions(response.permissions);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load service principals");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  // Picker groups, "Internal Services" first — the scopes platform services
  // most commonly need — then alphabetical.
  const groupedOptions = useMemo(() => {
    const groups = new Map<string, PermissionOption[]>();
    for (const option of permissionOptions) {
      const list = groups.get(option.group) ?? [];
      list.push(option);
      groups.set(option.group, list);
    }
    return Array.from(groups.entries()).sort(([a], [b]) =>
      a === "Internal Services" ? -1 : b === "Internal Services" ? 1 : a.localeCompare(b),
    );
  }, [permissionOptions]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setOpenGroups({ "Internal Services": true });
    setEditingId(null);
  };

  const openEdit = (row: ServicePrincipal) => {
    setForm({
      principal: row.principal,
      name: row.name,
      description: row.description ?? "",
      permissions: [...row.permissions],
    });
    setFormError(null);
    // Open every group that has a granted permission so the current state is visible.
    const open: Record<string, boolean> = { "Internal Services": true };
    for (const option of permissionOptions) {
      if (row.permissions.includes(option.permission)) open[option.group] = true;
    }
    setOpenGroups(open);
    setEditingId(row.id);
  };

  const togglePermission = (permission: string) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(permission)
        ? f.permissions.filter((p) => p !== permission)
        : [...f.permissions, permission],
    }));
  };

  const save = async () => {
    setIsSaving(true);
    setFormError(null);
    try {
      if (editingId) {
        await makeRequest(`/v1/admin/service-principals/${editingId}`, {
          method: "PATCH",
          body: { name: form.name, description: form.description || undefined, permissions: form.permissions },
        });
      } else {
        await makeRequest("/v1/admin/service-principals", {
          method: "POST",
          body: {
            principal: form.principal.trim(),
            name: form.name.trim(),
            description: form.description || undefined,
            permissions: form.permissions,
          },
        });
      }
      setEditingId(undefined);
      await load(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async (row: ServicePrincipal) => {
    if (!window.confirm(`Remove service principal "${row.name}" (${row.principal})? Its calls will be rejected.`)) {
      return;
    }
    try {
      await makeRequest(`/v1/admin/service-principals/${row.id}`, { method: "DELETE" });
      await load(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete");
    }
  };

  return (
    <View className="h-full w-full bg-gray-50 dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <Text className="text-xl font-semibold">Service Principals</Text>
          <Text className="text-sm text-gray-500 dark:text-gray-400">
            Workload Identity callers allowed on service-authenticated API endpoints
          </Text>
        </div>
        <Button
          variant="brand"
          type="button"
          onClick={openCreate}
        >
          Add principal
        </Button>
      </div>

      <div
        className="overflow-y-auto px-6 py-4 space-y-4"
        style={{ height: "calc(100% - 65px)" }}
      >
        {/* Create / edit form */}
        {editingId !== undefined && (
          <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3">
            <Text className="text-sm font-semibold">{editingId ? "Edit service principal" : "New service principal"}</Text>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InputField
                name="principal"
                label="Principal (GSA email)"
                placeholder="my-service-sa@prim-ai-production.iam.gserviceaccount.com"
                value={form.principal}
                isDisabled={!!editingId}
                onChange={(v: string) => setForm((f) => ({ ...f, principal: v }))}
              />
              <InputField
                name="name"
                label="Name"
                placeholder="primvoices-agents"
                value={form.name}
                onChange={(v: string) => setForm((f) => ({ ...f, name: v }))}
              />
            </div>
            <InputField
              name="description"
              label="Description (optional)"
              value={form.description}
              onChange={(v: string) => setForm((f) => ({ ...f, description: v }))}
            />
            <div>
              <div className="flex items-baseline gap-2 mb-2">
                <Text className="text-xs uppercase text-gray-400">Permissions</Text>
                <Text className="text-xs text-gray-400 dark:text-gray-500">
                  {form.permissions.length} selected — same registry as service accounts / PATs, no org binding
                </Text>
              </div>
              <div className="space-y-1 max-h-96 overflow-y-auto border border-gray-100 dark:border-gray-700 rounded p-2">
                {groupedOptions.map(([group, options]) => {
                  const selectedInGroup = options.filter((o: PermissionOption) => form.permissions.includes(o.permission)).length;
                  const isOpen = !!openGroups[group];
                  return (
                    <div key={group}>
                      <button
                        type="button"
                        className="w-full flex items-center justify-between py-1.5 px-1 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded"
                        onClick={() => setOpenGroups((g) => ({ ...g, [group]: !isOpen }))}
                      >
                        <span className="flex items-center gap-2">
                          <Icon
                            name={isOpen ? "expand_more" : "chevron_right"}
                            size={16}
                            className="text-gray-400"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{group}</span>
                        </span>
                        {selectedInGroup > 0 && <Badge variant="info">{selectedInGroup}</Badge>}
                      </button>
                      {isOpen && (
                        <div className="pl-7 pb-2 space-y-1.5">
                          {options.map((option: PermissionOption) => (
                            <label
                              key={option.permission}
                              className="flex items-start gap-2 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={form.permissions.includes(option.permission)}
                                onChange={() => togglePermission(option.permission)}
                              />
                              <span>
                                <span className="text-sm font-mono text-gray-800 dark:text-gray-200">
                                  {option.permission}
                                  {option.internal && (
                                    <span className="ml-1.5 text-[10px] uppercase text-gray-400">internal</span>
                                  )}
                                </span>
                                <span className="block text-xs text-gray-500 dark:text-gray-400">{option.description}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {formError && <Text className="text-sm text-error">{formError}</Text>}
            <div className="flex gap-2">
              <Button
                variant="brand"
                type="button"
                onClick={save}
                isLoading={isSaving}
                disabled={!form.name || (!editingId && !form.principal)}
              >
                {editingId ? "Save" : "Create"}
              </Button>
              <Button
                variant="ghost"
                type="button"
                onClick={() => setEditingId(undefined)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* List */}
        {isLoading ? (
          <View className="flex items-center justify-center py-20">
            <Spinner className="!text-brand" />
          </View>
        ) : error ? (
          <View className="flex items-center justify-center py-20">
            <Text className="text-error">{error}</Text>
          </View>
        ) : principals.length === 0 ? (
          <EmptyState
            icon="key"
            title="No service principals registered"
            description="Services calling the API with Workload Identity tokens are rejected until their principal is registered here with the permissions they need."
          />
        ) : (
          principals.map((row) => (
            <div
              key={row.id}
              className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Text className="text-sm font-medium">{row.name}</Text>
                  </div>
                  <span
                    className="block text-[11px] font-mono text-gray-400 dark:text-gray-500 truncate"
                    title={row.principal}
                  >
                    {row.principal}
                  </span>
                  {row.description && <Text className="block text-xs text-gray-500 dark:text-gray-400">{row.description}</Text>}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {row.permissions.length === 0 ? (
                      <Badge variant="warning">no permissions</Badge>
                    ) : (
                      row.permissions.map((permission) => (
                        <Badge
                          key={permission}
                          variant="info"
                        >
                          {permission}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => openEdit(row)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => remove(row)}
                  >
                    <Icon
                      name="delete"
                      size={16}
                      className="text-error"
                    />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </View>
  );
};
